import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { Callback1ArgPredicate } from "abweb/utils/callback/Callback1ArgPredicate";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { ErrorContext } from "abweb/utils/error/ErrorContext";
import { ErrorContextLogger } from "abweb/utils/error/ErrorContextLogger";
import { ErrorContextRenderBuffer } from "abweb/utils/error/ErrorContextRenderBuffer";
import { ErrorDispatcher } from "abweb/utils/error/ErrorDispatcher";
import { WrappedError } from "abweb/utils/error/WrappedError";
import { AlreadyHandledError } from "abweb/utils/PromiseUtils";
/**
 * Create a pipeline of ErrorContext processing that for each error:
 * <ul>
 * <li>logs the error
 * <li>initially records the error in a buffer until the following
 * <li>ui renderer is initialized, at which point it is used to display all buffered errors AND for all future errors
 * </ul>
 *
 * <p>It also supports registering IsErrorHandled predicate functions that should return true if the
 * predicate function handled the error - this supresses any further processing of the error.
 * If it returns false, the default log and render methods will be invoked on the
 * error context.
 *
 * <p>When the application shuts down, it should call the destroy method to unregister
 * error-related event listeners.
 */
export class LogAndRenderErrorPipeline {
  constructor(_renderHandler, _loggerHandler = new ErrorContextLogger()) {
    _defineProperty(this, "errorDispatcher", void 0);
    // See CAT-1205: Listener management for listeners that satisfy the IsErrorHandled type: (ErrorContext) => boolean
    _defineProperty(this, "errorHandledListeners", new Callback1ArgPredicate());
    _defineProperty(this, "errorDebouncer", new ErrorDebouncer());
    _defineProperty(this, "errorContextRenderBuffer", new ErrorContextRenderBuffer());
    this._renderHandler = _renderHandler;
    this._loggerHandler = _loggerHandler;
    // Defensive mem leak detection, as get weird test errors when multiple error dispatchers are all trying
    // to process errors thrown during tests
    if (LogAndRenderErrorPipeline.INSTANCE) {
      throw new Error("Need to invoke LogAndRenderErrorPipeline.pipeline.destroy() before creating a new LogAndRenderErrorPipeline," + " which is normally invoked as a side-effect of TMHServices.configure* workflows, or manually by the test.");
    }
    this.addErrorHandledListener(errCtx => this.errorDebouncer.isErrorHandledFn(errCtx));
    this.errorDispatcher = new ErrorDispatcher(this);
    LogAndRenderErrorPipeline.INSTANCE = this;
  }
  static get pipeline() {
    return LogAndRenderErrorPipeline.INSTANCE;
  }

  // Usually invoked by AbWebServices.resetServices even though it technically isn't a service (but is stateful)
  destroy() {
    this.errorDispatcher.destroy();
    // Below two are defensive in case 'this' instance is not gc'd correctly, it will allow referenced objects to be gc'd
    this.errorHandledListeners.removeAllCallbacks();
    this.errorContextRenderBuffer.resetErrorBuffer();
    LogAndRenderErrorPipeline.INSTANCE = undefined;
  }

  // noinspection JSUnusedGlobalSymbols
  set loggerHandler(errCtxHandler) {
    this._loggerHandler = errCtxHandler;
  }
  set renderHandler(errCtxHandler) {
    // Support transitioning from simple ui renderer (maybe html renderer or one that doesn't need all services initialized)
    // to full-featured ui renderer (typically available after i18n services, images services initialized).
    const oldRenderHandler = this._renderHandler;
    this._renderHandler = errCtxHandler;
    if (this._renderHandler && !oldRenderHandler) {
      this.errorContextRenderBuffer.forwardBufferedErrors(this._renderHandler);
    }
  }
  handleErrorContext(errCtx) {
    if (!this.isErrorHandled(errCtx)) {
      this._loggerHandler.handleErrorContext(errCtx);

      // Don't render if it is a logOnly error context
      if (!ClassUtils.isInstanceOf(errCtx, LogOnlyErrorContext)) {
        if (this._renderHandler) {
          this._renderHandler.handleErrorContext(errCtx);
        } else {
          this.errorContextRenderBuffer.handleErrorContext(errCtx);
        }
      }
    }
  }
  addErrorHandledListener(errorHandledListener) {
    return this.errorHandledListeners.addCallback(errorHandledListener);
  }

  /**
   * Errors thrown in 'promise chains' can be caught/handled by multiple code paths.
   * If you handle the error and do NOT want the other code path to ultimately cause the last chance error
   * handler to display it, invoke this method with the error (causes debounce logic to treat the catch
   * as the second throw of the same error, and the error is skipped).  Note that this works reliably only when
   * your 'catch block' is guaranteed to be triggered before the subsequent catch blocks.
   * Note that invoking AbErrorDialog.displayError automatically invokes this.
   * @param err
   */
  setLastErrorForDebounce(errUntyped) {
    this.errorDebouncer.setLastErrorForDebounce(errUntyped);
  }

  // 'catch' blocks receive 'err' which is untyped.  This allows for direct usage with 'caught' errors
  /** log and render the error */
  reportError(errUntyped) {
    const err = this.errorFactory(errUntyped);
    if (err) {
      this.handleErrorContext(new ErrorContext(err, null));
    }
  }

  // Should be rare to actually need to use this - only valid production use case is during app initialization it may
  // need to ensure that there are no uncaught exceptions (as they cause app initialization to fail) yet still want to
  // display the error directly.
  //
  // The other use case is for tests that similarly can't let exceptions be uncaught or else jasmine/karma notices
  // and fails the test.  Tests that want to verify that the error renderer is displayed when they
  // throw a 'global' exception (reaches ErrorDispatcher) can't do it, as jasmine/karma notices that the exception was
  // thrown and will fail the test.  Instead, up to test to explicitly catch exception and directly call reportError
  // (typically via try/catch or spyOn methods) - in that way, the exception is not noticed by the test machinery,

  // 'catch' blocks receive 'err' which is untyped.  This allows for direct usage with 'caught' errors
  /** Only log the error */
  logError(errUntyped) {
    const err = this.errorFactory(errUntyped);
    if (err) {
      this.handleErrorContext(new LogOnlyErrorContext(err, null));
    }
  }

  /** Coerce errUntyped into an Error (holding stack) unless error is to be skipped (already handled) */
  errorFactory(errUntyped) {
    return ClassUtils.isInstanceOf(errUntyped, AlreadyHandledError) // returns false if err is null
    ? null : WrappedError.errorFactory(errUntyped);
  }
  isErrorHandled(errCtx) {
    return this.errorHandledListeners.callUntilFirstTrue(errCtx);
  }

  /// Following only intended for testing //////

  getNumBufferedErrorsForTesting() {
    return this.errorContextRenderBuffer.errorBuffer.length;
  }
  resetBufferedErrorsForTesting() {
    this.errorContextRenderBuffer.resetErrorBuffer();
  }
}
_defineProperty(LogAndRenderErrorPipeline, "INSTANCE", void 0);
class LogOnlyErrorContext extends ErrorContext {
  constructor(error, moduleInfo) {
    super(error, moduleInfo);
  }
}
class ErrorDebouncer {
  constructor() {
    _defineProperty(this, "lastErrorUntyped", void 0);
  }
  isErrorHandledFn(errCtx) {
    // debounce: if an exception is thrown from a react callback, it is reported twice as it thrown by the callback,
    // and then somehow rethrown by react (more investigation needed!)

    // Note if it wrapped, need to get underlying error as '===' is checked for debouncing (will have different
    // WrappedError instances but same underlying value)
    const currError = ClassUtils.isInstanceOf(errCtx.error, WrappedError) ? errCtx.error.underlyingError : errCtx.error;
    if (this.lastErrorUntyped && currError && currError === this.lastErrorUntyped) {
      return true;
    }
    // new unique error
    this.lastErrorUntyped = currError;
    return false;
  }
  setLastErrorForDebounce(errUntyped) {
    this.lastErrorUntyped = errUntyped;
  }
}
//# sourceMappingURL=LogAndRenderErrorPipeline.js.map
